'use client';

import { c as _c } from "react/compiler-runtime";
import { MapPin, NavArrowLeft, NavArrowRight } from '@byway/icons';
import { clsx } from 'clsx';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button } from '~/components/Button';
import { Divider } from '~/components/Divider';
import { OverlayDrawer } from '~/components/Drawer';
import { SimpleSpinner, Spinner } from '~/components/Spinner/Spinner';
import { AnimatedPopoverContent } from '~/components/TripOverview/AnimatedPopoverContent';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import { api } from '~/libs/api';
import styles from './ChangeOriginModal.module.css';
const byLondonFirst = (a: {
  locationName: string;
}, b: {
  locationName: string;
}) => {
  if (a.locationName === 'London') return -1;
  if (b.locationName === 'London') return 1;
  return a.locationName.localeCompare(b.locationName);
};
type ChangeOriginModalProps = {
  isOpen: boolean;
  close: () => void;
  currentOrigin: string;
  tripId: string;
  onConfirm: (originName: string) => Promise<boolean>;
  presentation?: 'modal' | 'popover';
};
export function ChangeOriginModal(t0) {
  const $ = _c(67);
  const {
    isOpen,
    close,
    currentOrigin,
    tripId,
    onConfirm,
    presentation: t1
  } = t0;
  const presentation = t1 === undefined ? "modal" : t1;
  const t = useTranslations("journey");
  const commonT = useTranslations("common");
  const formsT = useTranslations("forms");
  const [pendingOrigin, setPendingOrigin] = useState(currentOrigin);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingOrigin, setSubmittingOrigin] = useState(null);
  const [activeRegion, setActiveRegion] = useState(null);
  const isTripUpdating = useIsTripUpdating();
  let t2;
  if ($[0] !== tripId) {
    t2 = {
      tripId
    };
    $[0] = tripId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isOpen) {
    t3 = {
      ...TRPC_SETTINGS.QUERY_NO_REFETCH,
      enabled: isOpen
    };
    $[2] = isOpen;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: t4,
    isLoading
  } = api.cms.origins.useQuery(t2, t3);
  const origins = t4 === undefined ? [] : t4;
  let t5;
  let t6;
  if ($[4] !== currentOrigin || $[5] !== isOpen) {
    t5 = () => {
      if (!isOpen) {
        return;
      }
      setPendingOrigin(currentOrigin);
      setActiveRegion(null);
    };
    t6 = [currentOrigin, isOpen];
    $[4] = currentOrigin;
    $[5] = isOpen;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  const ukOrigins = origins.filter(_temp).sort(byLondonFirst);
  const euOrigins = origins.filter(_temp2);
  const regionGroups = [{
    key: "UK" as const,
    label: t("origin_uk"),
    origins: ukOrigins
  }, {
    key: "EU" as const,
    label: t("origin_europe"),
    origins: euOrigins
  }].filter(_temp3);
  let t7;
  let t8;
  if ($[8] !== isOpen || $[9] !== presentation) {
    t7 = () => {
      if (presentation === "popover" || !isOpen) {
        return;
      }
      const root = document.documentElement;
      const viewport = window.visualViewport;
      const update = () => {
        const height = viewport?.height ?? window.innerHeight;
        root.style.setProperty("--visual-viewport-height", `${height}px`);
      };
      update();
      viewport?.addEventListener("resize", update);
      viewport?.addEventListener("scroll", update);
      return () => {
        viewport?.removeEventListener("resize", update);
        viewport?.removeEventListener("scroll", update);
        root.style.removeProperty("--visual-viewport-height");
      };
    };
    t8 = [isOpen, presentation];
    $[8] = isOpen;
    $[9] = presentation;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t9;
  if ($[12] !== close || $[13] !== onConfirm || $[14] !== presentation) {
    t9 = async locationName => {
      if (presentation === "popover") {
        setSubmittingOrigin(locationName);
        const success = await onConfirm(locationName);
        setSubmittingOrigin(null);
        if (success) {
          close();
        }
      } else {
        setPendingOrigin(locationName);
      }
    };
    $[12] = close;
    $[13] = onConfirm;
    $[14] = presentation;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleSelectOrigin = t9;
  let t10;
  if ($[16] !== close || $[17] !== onConfirm || $[18] !== pendingOrigin) {
    t10 = async () => {
      setIsSubmitting(true);
      const success_0 = await onConfirm(pendingOrigin);
      setIsSubmitting(false);
      if (success_0) {
        close();
      }
    };
    $[16] = close;
    $[17] = onConfirm;
    $[18] = pendingOrigin;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleConfirm = t10;
  let t11;
  if ($[20] !== currentOrigin) {
    t11 = () => {
      setActiveRegion(null);
      setPendingOrigin(currentOrigin);
    };
    $[20] = currentOrigin;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const handleBack = t11;
  let t12;
  if ($[22] !== currentOrigin) {
    t12 = () => setPendingOrigin(currentOrigin);
    $[22] = currentOrigin;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const handleCancel = t12;
  const activeGroup = regionGroups.find(group_0 => group_0.key === activeRegion);
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className={styles.popoverLoading}><Spinner /></div>;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const loadingView = t13;
  let t14;
  if ($[25] !== t) {
    t14 = t("no_origins");
    $[25] = t;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t14) {
    t15 = <p className={styles.helperText}>{t14}</p>;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const noOriginsView = t15;
  if (presentation === "popover") {
    if (!isOpen) {
      return null;
    }
    const popoverRegionView = regionGroups.length > 0 ? <ul className={styles.regionList} aria-label={t("origin_heading")}>{regionGroups.map(group_1 => <li key={group_1.key}><button type="button" className={styles.regionPill} onClick={() => setActiveRegion(group_1.key)}><span>{group_1.label}</span><NavArrowRight decorative={true} size="small" width={18} height={18} /></button></li>)}</ul> : noOriginsView;
    const t16 = activeRegion ?? "root";
    const t17 = activeGroup ? <><button type="button" className={styles.popoverBackHeading} onClick={() => setActiveRegion(null)}><NavArrowLeft decorative={true} size="small" width={18} height={18} /><span>{activeGroup.label}</span></button><ul className={styles.originList} aria-label={activeGroup.label}>{activeGroup.origins.map(origin_1 => {
          const isSelected = origin_1.locationName === currentOrigin;
          return <li key={origin_1.id}><button type="button" aria-current={isSelected ? "true" : undefined} className={styles.originPill} data-selected={isSelected || undefined} disabled={submittingOrigin !== null || isTripUpdating} onClick={() => handleSelectOrigin(origin_1.locationName)}><MapPin decorative={true} size="small" width={20} height={20} /><span>{origin_1.locationName}</span>{submittingOrigin === origin_1.locationName && <span className={styles.optionSpinner}><SimpleSpinner title="loading" width={16} height={16} /></span>}</button></li>;
        })}</ul></> : <><p className={styles.popoverHeading}>{t("origin_heading")}</p><Divider variant="horizontal" />{isLoading ? loadingView : popoverRegionView}</>;
    let t18;
    if ($[29] !== t16 || $[30] !== t17) {
      t18 = <AnimatedPopoverContent viewKey={t16} className={styles.popoverView}>{t17}</AnimatedPopoverContent>;
      $[29] = t16;
      $[30] = t17;
      $[31] = t18;
    } else {
      t18 = $[31];
    }
    return t18;
  }
  let t16;
  if ($[32] !== currentOrigin || $[33] !== pendingOrigin) {
    t16 = pendingOrigin !== currentOrigin && pendingOrigin.trim().length > 0;
    $[32] = currentOrigin;
    $[33] = pendingOrigin;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const showFooter = t16;
  const drawerRegionView = regionGroups.length > 0 ? <ul className={styles.drawerList} aria-label={t("origin_heading")}>{regionGroups.map(group_2 => <li key={group_2.key}><button type="button" className={styles.drawerRegionRow} onClick={() => setActiveRegion(group_2.key)}><span>{group_2.label}</span><NavArrowRight decorative={true} size="small" width={18} height={18} /></button></li>)}</ul> : noOriginsView;
  const drawerOriginsView = activeGroup ? <ul className={styles.drawerList} aria-label={activeGroup.label}>{activeGroup.origins.map(origin_2 => {
      const isSelected_0 = origin_2.locationName === pendingOrigin;
      return <li key={origin_2.id}><button type="button" aria-current={isSelected_0 ? "true" : undefined} className={styles.drawerOriginRow} data-selected={isSelected_0 || undefined} disabled={isSubmitting || isTripUpdating} onClick={() => handleSelectOrigin(origin_2.locationName)}><MapPin decorative={true} size="small" width={20} height={20} /><span>{origin_2.locationName}</span></button></li>;
    })}</ul> : drawerRegionView;
  let t17;
  if ($[35] !== activeGroup || $[36] !== commonT || $[37] !== handleBack) {
    t17 = activeGroup && <button type="button" className={styles.drawerBack} onClick={handleBack} aria-label={commonT("back")}><NavArrowLeft decorative={true} size="small" width={18} height={18} /></button>;
    $[35] = activeGroup;
    $[36] = commonT;
    $[37] = handleBack;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== activeGroup || $[40] !== t) {
    t18 = activeGroup ? activeGroup.label : t("origin_heading");
    $[39] = activeGroup;
    $[40] = t;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t18) {
    t19 = <span className={styles.drawerTitle}>{t18}</span>;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t17 || $[45] !== t19) {
    t20 = <div className={styles.drawerHeader}>{t17}{t19}</div>;
    $[44] = t17;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  const drawerHeader = t20;
  let t21;
  if ($[47] !== t) {
    t21 = t("booking_choose_origin");
    $[47] = t;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Divider variant="horizontal" />;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  const t23 = isLoading ? loadingView : drawerOriginsView;
  let t24;
  if ($[50] !== formsT || $[51] !== handleCancel || $[52] !== handleConfirm || $[53] !== isSubmitting || $[54] !== isTripUpdating || $[55] !== showFooter || $[56] !== t) {
    t24 = showFooter && <div className={styles.drawerFooter}><div className={styles.drawerFooterCol}><Button variant="pill" size="fullWidth" onPress={handleCancel} isDisabled={isSubmitting}>{formsT("cancel")}</Button></div><div className={clsx(styles.drawerFooterCol, styles.drawerFooterConfirm)}><Button variant="primary" size="fullWidth" onPress={handleConfirm} isDisabled={isTripUpdating} isLoading={isSubmitting} dataTestId="change-origin-confirm">{t("confirm")}</Button></div></div>;
    $[50] = formsT;
    $[51] = handleCancel;
    $[52] = handleConfirm;
    $[53] = isSubmitting;
    $[54] = isTripUpdating;
    $[55] = showFooter;
    $[56] = t;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== t23 || $[59] !== t24) {
    t25 = <div className={styles.drawerRoot}>{t22}{t23}{t24}</div>;
    $[58] = t23;
    $[59] = t24;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== close || $[62] !== drawerHeader || $[63] !== isOpen || $[64] !== t21 || $[65] !== t25) {
    t26 = <OverlayDrawer isOpen={isOpen} close={close} ariaLabel={t21} headerContent={drawerHeader} dataTestId="ChangeOriginDrawer">{t25}</OverlayDrawer>;
    $[61] = close;
    $[62] = drawerHeader;
    $[63] = isOpen;
    $[64] = t21;
    $[65] = t25;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  return t26;
}
function _temp3(group) {
  return group.origins.length > 0;
}
function _temp2(origin_0) {
  return origin_0.country?.countryId !== "UK";
}
function _temp(origin) {
  return origin.country?.countryId === "UK";
}